
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { ElementWrapper } from '@cloudscape-design/test-utils-core/dom';
import { appendSelector } from '@cloudscape-design/test-utils-core/utils';

export { ElementWrapper };

import AvatarWrapper from './avatar';
import ChatBubbleWrapper from './chat-bubble';
import LoadingBarWrapper from './loading-bar';
import SupportPromptGroupWrapper from './support-prompt-group';


export { AvatarWrapper };
export { ChatBubbleWrapper };
export { LoadingBarWrapper };
export { SupportPromptGroupWrapper };

declare module '@cloudscape-design/test-utils-core/dist/dom' {
   interface ElementWrapper {
    
/**
 * Returns the wrapper of the first Avatar that matches the specified CSS selector.
 * If no CSS selector is specified, returns the wrapper of the first Avatar.
 * If no matching Avatar is found, returns `null`.
 *
 * @param {string} [selector] CSS Selector
 * @returns {AvatarWrapper | null}
 */
findAvatar(selector?: string): AvatarWrapper | null;

/**
 * Returns an array of Avatar wrapper that matches the specified CSS selector.
 * If no CSS selector is specified, returns all of the Avatars inside the current wrapper.
 * If no matching Avatar is found, returns an empty array.
 *
 * @param {string} [selector] CSS Selector
 * @returns {Array<AvatarWrapper>}
 */
findAllAvatars(selector?: string): Array<AvatarWrapper>;

/**
 * Returns the wrapper of the closest parent Avatar for the current element,
 * or the element itself if it is an instance of Avatar.
 * If no Avatar is found, returns `null`.
 *
 * @returns {AvatarWrapper | null}
 */
findClosestAvatar(): AvatarWrapper | null;
/**
 * Returns the wrapper of the first ChatBubble that matches the specified CSS selector.
 * If no CSS selector is specified, returns the wrapper of the first ChatBubble.
 * If no matching ChatBubble is found, returns `null`.
 *
 * @param {string} [selector] CSS Selector
 * @returns {ChatBubbleWrapper | null}
 */
findChatBubble(selector?: string): ChatBubbleWrapper | null;

/**
 * Returns an array of ChatBubble wrapper that matches the specified CSS selector.
 * If no CSS selector is specified, returns all of the ChatBubbles inside the current wrapper.
 * If no matching ChatBubble is found, returns an empty array.
 *
 * @param {string} [selector] CSS Selector
 * @returns {Array<ChatBubbleWrapper>}
 */
findAllChatBubbles(selector?: string): Array<ChatBubbleWrapper>;

/**
 * Returns the wrapper of the closest parent ChatBubble for the current element,
 * or the element itself if it is an instance of ChatBubble.
 * If no ChatBubble is found, returns `null`.
 *
 * @returns {ChatBubbleWrapper | null}
 */
findClosestChatBubble(): ChatBubbleWrapper | null;
/**
 * Returns the wrapper of the first LoadingBar that matches the specified CSS selector.
 * If no CSS selector is specified, returns the wrapper of the first LoadingBar.
 * If no matching LoadingBar is found, returns `null`.
 *
 * @param {string} [selector] CSS Selector
 * @returns {LoadingBarWrapper | null}
 */
findLoadingBar(selector?: string): LoadingBarWrapper | null;

/**
 * Returns an array of LoadingBar wrapper that matches the specified CSS selector.
 * If no CSS selector is specified, returns all of the LoadingBars inside the current wrapper.
 * If no matching LoadingBar is found, returns an empty array.
 *
 * @param {string} [selector] CSS Selector
 * @returns {Array<LoadingBarWrapper>}
 */
findAllLoadingBars(selector?: string): Array<LoadingBarWrapper>;

/**
 * Returns the wrapper of the closest parent LoadingBar for the current element,
 * or the element itself if it is an instance of LoadingBar.
 * If no LoadingBar is found, returns `null`.
 *
 * @returns {LoadingBarWrapper | null}
 */
findClosestLoadingBar(): LoadingBarWrapper | null;
/**
 * Returns the wrapper of the first SupportPromptGroup that matches the specified CSS selector.
 * If no CSS selector is specified, returns the wrapper of the first SupportPromptGroup.
 * If no matching SupportPromptGroup is found, returns `null`.
 *
 * @param {string} [selector] CSS Selector
 * @returns {SupportPromptGroupWrapper | null}
 */
findSupportPromptGroup(selector?: string): SupportPromptGroupWrapper | null;

/**
 * Returns an array of SupportPromptGroup wrapper that matches the specified CSS selector.
 * If no CSS selector is specified, returns all of the SupportPromptGroups inside the current wrapper.
 * If no matching SupportPromptGroup is found, returns an empty array.
 *
 * @param {string} [selector] CSS Selector
 * @returns {Array<SupportPromptGroupWrapper>}
 */
findAllSupportPromptGroups(selector?: string): Array<SupportPromptGroupWrapper>;

/**
 * Returns the wrapper of the closest parent SupportPromptGroup for the current element,
 * or the element itself if it is an instance of SupportPromptGroup.
 * If no SupportPromptGroup is found, returns `null`.
 *
 * @returns {SupportPromptGroupWrapper | null}
 */
findClosestSupportPromptGroup(): SupportPromptGroupWrapper | null;
   }
}


ElementWrapper.prototype.findAvatar = function(selector) {
  let rootSelector = `.${AvatarWrapper.rootSelector}`;
  if("legacyRootSelector" in AvatarWrapper && AvatarWrapper.legacyRootSelector){
    rootSelector = `:is(.${AvatarWrapper.rootSelector}, .${AvatarWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, AvatarWrapper);
};

ElementWrapper.prototype.findAllAvatars = function(selector) {
  return this.findAllComponents(AvatarWrapper, selector);
};
ElementWrapper.prototype.findChatBubble = function(selector) {
  let rootSelector = `.${ChatBubbleWrapper.rootSelector}`;
  if("legacyRootSelector" in ChatBubbleWrapper && ChatBubbleWrapper.legacyRootSelector){
    rootSelector = `:is(.${ChatBubbleWrapper.rootSelector}, .${ChatBubbleWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, ChatBubbleWrapper);
};

ElementWrapper.prototype.findAllChatBubbles = function(selector) {
  return this.findAllComponents(ChatBubbleWrapper, selector);
};
ElementWrapper.prototype.findLoadingBar = function(selector) {
  let rootSelector = `.${LoadingBarWrapper.rootSelector}`;
  if("legacyRootSelector" in LoadingBarWrapper && LoadingBarWrapper.legacyRootSelector){
    rootSelector = `:is(.${LoadingBarWrapper.rootSelector}, .${LoadingBarWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, LoadingBarWrapper);
};

ElementWrapper.prototype.findAllLoadingBars = function(selector) {
  return this.findAllComponents(LoadingBarWrapper, selector);
};
ElementWrapper.prototype.findSupportPromptGroup = function(selector) {
  let rootSelector = `.${SupportPromptGroupWrapper.rootSelector}`;
  if("legacyRootSelector" in SupportPromptGroupWrapper && SupportPromptGroupWrapper.legacyRootSelector){
    rootSelector = `:is(.${SupportPromptGroupWrapper.rootSelector}, .${SupportPromptGroupWrapper.legacyRootSelector})`;
  }
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findComponent(selector ? appendSelector(selector, rootSelector) : rootSelector, SupportPromptGroupWrapper);
};

ElementWrapper.prototype.findAllSupportPromptGroups = function(selector) {
  return this.findAllComponents(SupportPromptGroupWrapper, selector);
};

ElementWrapper.prototype.findClosestAvatar = function() {
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findClosestComponent(AvatarWrapper);
};
ElementWrapper.prototype.findClosestChatBubble = function() {
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findClosestComponent(ChatBubbleWrapper);
};
ElementWrapper.prototype.findClosestLoadingBar = function() {
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findClosestComponent(LoadingBarWrapper);
};
ElementWrapper.prototype.findClosestSupportPromptGroup = function() {
  // casting to 'any' is needed to avoid this issue with generics
  // https://github.com/microsoft/TypeScript/issues/29132
  return (this as any).findClosestComponent(SupportPromptGroupWrapper);
};

export default function wrapper(root: Element = document.body) {
  if (document && document.body && !document.body.contains(root)) {
    console.warn('[AwsUi] [test-utils] provided element is not part of the document body, interactions may work incorrectly')
  };
  return new ElementWrapper(root);
}
